import {
  AlertTriangle,
  BadgeCheck,
  Bell,
  ChartNoAxesCombined,
  CircleCheckBig,
  FileWarning,
  Flag,
  Landmark,
  MessageCircleQuestion,
  MessageSquareWarning,
  PenLine,
  Radio,
  Signature,
  Sparkles,
  UserPlus,
  UserRound,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Notification } from './types';

/**
 * How each of the server's event types looks in the inbox.
 *
 * THE FEED CARRIES TWO AXES and this file draws both. `tone` is the server's
 * 3-value severity (ready / attention / info); `type` is its ~14-value event
 * kind. The panel used to draw only the tone, so "New client added", "New
 * strategy created" and "Client is waiting on their documents" were three
 * identical grey `Info` circles — an inbox where a dozen different things all
 * looked like one thing. Scanning twelve rows was reading, not glancing, which
 * is the one job a list that short has.
 *
 * So IDENTITY leads and SEVERITY backs it up. The two are allowed to disagree:
 * `client_comments` is "attention" on the server, and here it is a message
 * icon in amber rather than the red triangle a failed backtest gets, because
 * "a client disputed an answer" and "a compute job crashed" are not the same
 * event even when they share an urgency.
 *
 * (This replaced a `tone.tsx` that mapped severity alone. It had one caller
 * left once the panel and the dropdown both moved here, so it was folded in
 * rather than left as a second, quieter answer to the same question.)
 *
 * COLOURS ARE PAIRED, always `x-600 dark:x-400`. A single `x-600` on the dark
 * popover surface (#1e1b31) drops under 4.5:1 for every hue here; the 400 step
 * clears it. Do not collapse a pair to one value.
 *
 * THE KEYS ARE THE SERVER'S STRINGS, and the list is not exhaustive by
 * construction — `service.py` types them as bare `str` and mints new ones
 * without touching this file. That is exactly why `eventMetaFor` takes the
 * TONE as well: an event type nobody has drawn yet still has a severity the
 * server asserted, and falling back to it keeps a new `attention` event red
 * instead of demoting it to a neutral bell the day it ships.
 */
export interface EventMeta {
  Icon: LucideIcon;
  /** Icon colour. */
  className: string;
  /** Soft fill behind the icon, same hue. */
  chipClassName: string;
}

const NEUTRAL: EventMeta = {
  Icon: Bell,
  className: 'text-muted-foreground',
  chipClassName: 'bg-muted',
};

/**
 * Build the look for an event from an icon and a hue.
 *
 * EXPORTED, because the client's activity feed draws forty more event types
 * with the same rule and the same paired colours. A second builder there
 * would be a second place for the `x-600 dark:x-400` pairing to go wrong, and
 * the pairing is the whole reason this function exists rather than callers
 * writing the classes out themselves.
 */
export function eventMetaOf(
  Icon: LucideIcon,
  hue: 'emerald' | 'red' | 'amber' | 'violet' | 'blue',
): EventMeta {
  // Written out rather than interpolated: Tailwind scans source text for
  // literal class names, so `text-${hue}-600` would be compiled away and every
  // icon here would render with no colour at all.
  const colours = {
    emerald: ['text-emerald-600 dark:text-emerald-400', 'bg-emerald-500/10'],
    red: ['text-red-600 dark:text-red-400', 'bg-red-500/10'],
    amber: ['text-amber-600 dark:text-amber-400', 'bg-amber-500/10'],
    violet: ['text-violet-600 dark:text-violet-400', 'bg-violet-500/10'],
    blue: ['text-blue-600 dark:text-blue-400', 'bg-blue-500/10'],
  }[hue];
  return { Icon, className: colours[0], chipClassName: colours[1] };
}

// `| undefined` in the value type, because the key type is `string` and every
// lookup is a miss waiting to happen — `service.py` mints event types without
// touching this file. A plain `Record<string, EventMeta>` would type the miss
// as a hit, and the fallback below as dead code.
export const EVENT_META: Record<string, EventMeta | undefined> = {
  // Strategies and their runs.
  backtest_completed: eventMetaOf(ChartNoAxesCombined, 'emerald'),
  backtest_failed: eventMetaOf(AlertTriangle, 'red'),
  strategy_created: eventMetaOf(Sparkles, 'violet'),
  live_model_published: eventMetaOf(Radio, 'violet'),

  // People inside the firm.
  member_joined: eventMetaOf(UserPlus, 'blue'),

  // Clients and their accounts.
  client_added: eventMetaOf(UserRound, 'blue'),
  account_opened: eventMetaOf(Landmark, 'emerald'),
  client_question: eventMetaOf(MessageCircleQuestion, 'amber'),

  // Onboarding. Five of the seven are things the CLIENT did that the firm now
  // has to answer, which is why they are amber rather than red — someone is
  // waiting, nothing is broken. The two blue ones are submissions: the client
  // is done and the file has moved on, so they read as progress rather than as
  // a request. They are two rather than one because the advisor's next act
  // differs — open the accounts, or send the signing links.
  client_comments: eventMetaOf(MessageSquareWarning, 'amber'),
  correction_requested: eventMetaOf(PenLine, 'amber'),
  data_gap: eventMetaOf(FileWarning, 'amber'),
  needs_review: eventMetaOf(Flag, 'amber'),
  submitted_awaiting_firm: eventMetaOf(BadgeCheck, 'blue'),
  submitted_for_signing: eventMetaOf(Signature, 'blue'),
  completed: eventMetaOf(CircleCheckBig, 'emerald'),
};

const TONE_FALLBACK: Record<Notification['type'], EventMeta> = {
  success: eventMetaOf(CircleCheckBig, 'emerald'),
  error: eventMetaOf(AlertTriangle, 'red'),
  info: NEUTRAL,
};

/**
 * The look for an event type.
 *
 * Identity first, severity second, neutral last. `tone` is optional so callers
 * that genuinely only have a type still work, but passing it is the difference
 * between a new server-side failure showing up red and showing up as a bell.
 */
export function eventMetaFor(
  eventType: string | undefined,
  tone?: string,
): EventMeta {
  if (eventType !== undefined) {
    const known = EVENT_META[eventType];
    if (known) return known;
  }
  // A tone the UI has never heard of falls through to NEUTRAL rather than
  // being coerced: `Notification['type']` is a union, but the value arrives
  // from `notificationsRepository`, which maps the wire's bare `str` and can
  // pass through anything the server invents.
  if (tone === 'success' || tone === 'error' || tone === 'info') {
    return TONE_FALLBACK[tone];
  }
  return NEUTRAL;
}
